import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { money } from '../lib/format'

/**
 * Small visual pieces the design system does not ship, built on its tokens.
 * Bars are one colour per series (slot 1 unless told otherwise), 4px rounded
 * data ends, 2px gaps, values in text ink, never in the series colour.
 */

/** Horizontal bars: long labels never collide, and one series is one colour. */
export function HBars({ title, subtitle, rows, format = (n) => money(n), empty = 'Nothing to show yet.', slot = 1 }: {
  title?: ReactNode; subtitle?: ReactNode
  rows: { label: string; value: number; note?: ReactNode }[]
  format?: (n: number) => string; empty?: string; slot?: number
}) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <figure className="viz" style={{ margin: 0 }}>
      {title && <figcaption className="viz-title">{title}</figcaption>}
      {subtitle && <p className="viz-sub">{subtitle}</p>}
      {rows.length === 0 ? <p className="muted" style={{ marginTop: 'var(--s-5)' }}>{empty}</p> : (
        <div className="hbars">
          {rows.map((r) => (
            <div className="hbar-row" key={r.label} title={`${r.label}: ${format(r.value)}`}>
              <span className="hbar-label">{r.label}</span>
              <span className="hbar-track"><i style={{ width: `${r.value > 0 ? Math.max(1.5, (r.value / max) * 100) : 0}%`, background: `var(--viz-${slot})` }} /></span>
              <span className="hbar-value num">{format(r.value)}</span>
              {r.note && <span className="hbar-note muted">{r.note}</span>}
            </div>
          ))}
        </div>
      )}
    </figure>
  )
}

/** One whole split into parts, as a single bar with a legend that carries the numbers. */
export function SplitBar({ title, subtitle, headline, parts, format = (n) => money(n) }: {
  title?: ReactNode; subtitle?: ReactNode; headline?: ReactNode
  parts: { label: string; value: number; slot: number }[]; format?: (n: number) => string
}) {
  const sum = parts.reduce((a, p) => a + Math.max(0, p.value), 0) || 1
  return (
    <figure className="viz" style={{ margin: 0 }}>
      {title && <figcaption className="viz-title">{title}</figcaption>}
      {subtitle && <p className="viz-sub">{subtitle}</p>}
      {headline && <div className="split-total">{headline}</div>}
      <div className="split-bar" role="img" aria-label={parts.map((p) => `${p.label} ${format(p.value)}`).join(', ')}>
        {parts.filter((p) => p.value > 0).map((p) => (
          <span key={p.label} title={`${p.label}: ${format(p.value)}`} style={{ flexGrow: p.value / sum, background: `var(--viz-${p.slot})` }} />
        ))}
      </div>
      <div className="split-legend">
        {parts.map((p) => (
          <div key={p.label}>
            <span className="viz-swatch" style={{ background: `var(--viz-${p.slot})` }} />
            <span>{p.label}</span>
            <strong className="num">{format(p.value)}</strong>
            <span className="muted num">{Math.round((Math.max(0, p.value) / sum) * 100)}%</span>
          </div>
        ))}
      </div>
    </figure>
  )
}

/** Each stage as a bar sized to the first, with the share that made it from the stage before. */
export function Funnel({ title, subtitle, steps }: {
  title?: ReactNode; subtitle?: ReactNode; steps: { label: string; value: number; hint?: string }[]
}) {
  const top = Math.max(1, steps[0]?.value || 0)
  return (
    <figure className="viz" style={{ margin: 0 }}>
      {title && <figcaption className="viz-title">{title}</figcaption>}
      {subtitle && <p className="viz-sub">{subtitle}</p>}
      <ol className="funnel">
        {steps.map((s, i) => {
          const prev = i > 0 ? steps[i - 1].value : null
          return (
            <li key={s.label}>
              <div className="funnel-head">
                <span>{s.label}</span>
                <strong className="num">{s.value.toLocaleString('en-US')}</strong>
              </div>
              <span className="funnel-track"><i style={{ width: `${s.value > 0 ? Math.max(1.5, (s.value / top) * 100) : 0}%` }} /></span>
              <span className="funnel-note muted">{prev == null ? s.hint : prev > 0 ? `${Math.round((s.value / prev) * 100)}% of the step before${s.hint ? `. ${s.hint}` : ''}` : s.hint}</span>
            </li>
          )
        })}
      </ol>
    </figure>
  )
}

/** A share as a short track. Text says the number; the bar is for scanning a column. */
export function RateBar({ num: n, den, label }: { num: number; den: number; label?: string }) {
  if (!den) return <span className="muted">none yet</span>
  const pct = Math.round((n / den) * 100)
  return (
    <span className="ratebar" title={`${n} of ${den}`}>
      <span className="ratebar-track"><i style={{ width: `${pct}%` }} /></span>
      <span className="num">{pct}%</span>
      {label && <span className="muted">{label}</span>}
    </span>
  )
}

/** Four seats per group: filled dots are reps, open ones are room. */
export function SlotDots({ used, max = 4 }: { used: number; max?: number }) {
  return (
    <span className="slotdots" aria-label={`${used} of ${max} rep seats used`} title={`${used} of ${max} rep seats used`}>
      {Array.from({ length: max }, (_, i) => <i key={i} className={i < used ? 'on' : undefined} />)}
    </span>
  )
}

/** Views as tiles: the count is the reason to click. */
export function FilterTiles<T extends string>({ value, onChange, tiles }: {
  value: T; onChange: (v: T) => void
  tiles: { value: T; label: string; count: number | undefined; tone?: 'warn' | 'neg' | 'pos'; hint?: string }[]
}) {
  return (
    <div className="filter-tiles" role="tablist">
      {tiles.map((t) => (
        <button key={t.value} role="tab" aria-selected={t.value === value} className={`filter-tile${t.tone && t.count ? ` tone-${t.tone}` : ''}`} onClick={() => onChange(t.value)}>
          <span className="filter-tile-label">{t.label}</span>
          <span className="filter-tile-count num">{t.count ?? ''}</span>
          {t.hint && <span className="filter-tile-hint">{t.hint}</span>}
        </button>
      ))}
    </div>
  )
}

/** Things someone should act on, each a link. Nothing to act on says so. */
export function AttentionList({ items }: { items: { count: number; label: string; to: string; tone?: 'warn' | 'neg' }[] }) {
  const live = items.filter((i) => i.count > 0)
  if (!live.length) return <p className="muted" style={{ margin: 'var(--s-3) 0 0' }}>Nothing needs attention.</p>
  return (
    <ul className="attention">
      {live.map((i) => (
        <li key={i.label}>
          <Link to={i.to}>
            <span className={`attention-n num tone-${i.tone || 'warn'}`}>{i.count}</span>
            <span>{i.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
